import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
    Card,
    Table,
    Button,
    Space,
    Select,
    message,
    Tag,
    Drawer,
    Typography,
    Row,
    Col,
    Divider,
    Descriptions,
    Checkbox,
    Empty,
    Tooltip,
    DatePicker,
    Alert,
    Spin
} from 'antd';
import {
    EyeOutlined,
    ReloadOutlined,
    DashboardOutlined,
    ExportOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import settlementApi from '../../../api/settlementApi';
import aircraftApi from '../../../api/v3/aircraftApi';
import AircraftSelector from '../../../components/common/AircraftSelector';
import AircraftTag from '../../../components/AircraftTag';
import { useTimeZone, TIMEZONE_OPTIONS, getTimezoneByMode } from '../../../contexts/TimeZoneContext';
import { TimezoneConverter, getTimezoneOffset } from '../../../utils/timezoneUtils';
import { exportProfitReportToExcel } from '../../../utils/excelUtils';
import './ProfitReportManagementPage.css';

// 扩展dayjs插件
dayjs.extend(utc);
dayjs.extend(timezone);

const { Title, Text } = Typography;
const { Option } = Select;

/**
 * 利润报表管理页面（航线实例成本分析）
 * 
 * 展示批次核算完成后的完整航线实例，提供详细的费用类型分解。
 * 数据来源：已完成的批次核算记录
 * 
 * 新增功能：
 * - 可展开表格显示航段明细
 * - 主表格显示所有费用类型汇总
 * - 航段明细显示所有费用类型，按段次排列
 */
const ProfitReportManagementPage = () => {
    // 时区上下文
    const { timeZone: timeZoneMode, setTimeZone, getTimeZoneDisplayName, airportsMap } = useTimeZone();
    const navigate = useNavigate();
    
    // 数据状态
    const [instances, setInstances] = useState([]);
    const [routes, setRoutes] = useState([]);
    const [allAircrafts, setAllAircrafts] = useState([]);           // 全量飞机列表（联动过滤的基础数据）

    // 筛选联动状态（飞机号 ↔ 航线 双向联动）
    const [linkedRouteIds, setLinkedRouteIds] = useState(null);         // null=不限制；Set=允许的航线ID
    const [linkedAircraftRegs, setLinkedAircraftRegs] = useState(null); // null=不限制；Set=允许的飞机号
    const [filterOptionsLoading, setFilterOptionsLoading] = useState(false);
    const filterOptionsRequestRef = useRef(0);                          // 请求序号，丢弃过期响应
    
    // UI状态
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [detailDrawerVisible, setDetailDrawerVisible] = useState(false);
    const [selectedInstance, setSelectedInstance] = useState(null);
    const [dateRangeKey, setDateRangeKey] = useState(0);
    const [exporting, setExporting] = useState(false);
    
    // 筛选条件
    const [filters, setFilters] = useState({
        sales_route_id: null,
        complete_only: true,
        aircraft_reg: [],
        date_range: null,
    });
    
    // 滚动加载（接口仍按 page 拉取，前端追加）
    const LIST_PAGE_SIZE = 30;
    const [listPage, setListPage] = useState(1);
    const [total, setTotal] = useState(0);
    const listGenerationRef = useRef(0);
    const loadingRef = useRef(false);       // 与 loading 同步，避免闭包过期导致重置中仍触发 loadMore
    const loadingMoreRef = useRef(false);
    const hasMore = instances.length < total;

    useEffect(() => {
        fetchRoutes();
        fetchAllAircrafts();
    }, []);

    useEffect(() => {
        fetchInstances({ page: 1, reset: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters]);

    // 筛选联动：飞机号/航线/日期任一变化时，重新计算两个下拉的可选集合
    useEffect(() => {
        const selectedRegs = filters.aircraft_reg || [];
        const hasAircraft = selectedRegs.length > 0;
        const hasRoute = !!filters.sales_route_id;
        const hasDate = filters.date_range && filters.date_range.length === 2;

        // 没有任何约束条件：恢复不限制，使在途请求失效
        if (!hasAircraft && !hasRoute && !hasDate) {
            filterOptionsRequestRef.current += 1;
            setLinkedRouteIds(null);
            setLinkedAircraftRegs(null);
            setFilterOptionsLoading(false);
            return undefined;
        }

        const requestId = filterOptionsRequestRef.current + 1;
        filterOptionsRequestRef.current = requestId;
        // 查询期间下拉呈现等待状态；结果返回前的旧选项若失效，由下方清理逻辑兜底
        setFilterOptionsLoading(true);

        // 防抖：多选连续勾选时合并为一次请求
        const timer = setTimeout(async () => {
            try {
                // 日期条件同样约束联动集合，保证下拉选项在当前筛选组合下必有数据
                const params = { complete_only: filters.complete_only };
                if (hasAircraft) params.aircraft_reg = selectedRegs;
                if (hasRoute) params.sales_route_id = filters.sales_route_id;
                if (filters.date_range && filters.date_range.length === 2) {
                    params.start_date = filters.date_range[0];
                    params.end_date = filters.date_range[1];
                }

                const response = await settlementApi.routeInstances.getFilterOptions(params);
                if (filterOptionsRequestRef.current !== requestId) return; // 过期响应丢弃

                const data = response.data?.data || {};
                const routeIdSet = Array.isArray(data.route_ids) ? new Set(data.route_ids) : null;
                const aircraftRegSet = Array.isArray(data.aircraft_regs) ? new Set(data.aircraft_regs) : null;
                setLinkedRouteIds(routeIdSet);
                setLinkedAircraftRegs(aircraftRegSet);

                // 失效清理：已选项不在联动集合中时自动清除并提示
                if (routeIdSet && hasRoute && !routeIdSet.has(filters.sales_route_id)) {
                    message.info('已选航线在当前筛选条件下没有数据，已自动清除');
                    handleFilterChange('sales_route_id', null);
                }
                if (aircraftRegSet && hasAircraft) {
                    const validRegs = selectedRegs.filter((reg) => aircraftRegSet.has(reg));
                    if (validRegs.length !== selectedRegs.length) {
                        message.info('部分已选飞机在当前筛选条件下没有数据，已自动清除');
                        handleFilterChange('aircraft_reg', validRegs);
                    }
                }
            } catch (error) {
                console.error('获取筛选联动选项失败:', error);
                // 联动查询失败时不限制选项，保证筛选功能仍然可用
                if (filterOptionsRequestRef.current === requestId) {
                    setLinkedRouteIds(null);
                    setLinkedAircraftRegs(null);
                }
            } finally {
                if (filterOptionsRequestRef.current === requestId) {
                    setFilterOptionsLoading(false);
                }
            }
        }, 300);

        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters.aircraft_reg, filters.sales_route_id, filters.complete_only, filters.date_range]);

    // 获取航线列表
    const fetchRoutes = async () => {
        try {
            const response = await settlementApi.salesRoutes.getSalesRoutes();
            setRoutes(response.data || []);
        } catch (error) {
            console.error('获取航线列表失败:', error);
        }
    };

    // 获取全量飞机列表（用于联动过滤后传入 AircraftSelector）
    const fetchAllAircrafts = async () => {
        try {
            const response = await aircraftApi.getAircraftList({ page_size: 500 });
            setAllAircrafts(response?.data?.items || []);
        } catch (error) {
            console.error('获取飞机列表失败:', error);
        }
    };

    // 获取航线实例列表（reset=替换；否则追加，用于滚动加载）
    const fetchInstances = async ({ page = 1, reset = true } = {}) => {
        let generation;
        if (reset) {
            // 抬升 generation：使在途 loadMore / 旧汇总全部失效
            generation = listGenerationRef.current + 1;
            listGenerationRef.current = generation;
            loadingMoreRef.current = false;
            loadingRef.current = true;
            setLoading(true);
            setLoadingMore(false);
        } else {
            // 重置进行中或正在追加时禁止再开 loadMore（用 ref，避免 loading 闭包过期）
            if (loadingMoreRef.current || loadingRef.current) return;
            generation = listGenerationRef.current;
            if (generation === 0) return;
            loadingMoreRef.current = true;
            setLoadingMore(true);
        }

        try {
            const params = {
                sales_route_id: filters.sales_route_id,
                complete_only: filters.complete_only,
                page,
                page_size: LIST_PAGE_SIZE,
            };

            if (filters.aircraft_reg && filters.aircraft_reg.length > 0) {
                params.aircraft_reg = filters.aircraft_reg;
            }

            if (filters.date_range && filters.date_range.length === 2) {
                params.start_date = filters.date_range[0];
                params.end_date = filters.date_range[1];
            }

            const response = await settlementApi.routeInstances.getRouteInstances(params);
            if (generation !== listGenerationRef.current) return;

            const items = response.data.items || [];
            const nextTotal = response.data.total || 0;
            setInstances((prev) => (reset ? items : [...prev, ...items]));
            setTotal(nextTotal);
            setListPage(page);
        } catch (error) {
            if (generation !== listGenerationRef.current) return;
            console.error('获取航线实例列表失败:', error);
            message.error('加载数据失败');
        } finally {
            if (generation === listGenerationRef.current) {
                loadingRef.current = false;
                setLoading(false);
                setLoadingMore(false);
                loadingMoreRef.current = false;
            }
        }
    };

    const loadMoreInstances = () => {
        if (!hasMore || loadingRef.current || loadingMoreRef.current) return;
        fetchInstances({ page: listPage + 1, reset: false });
    };

    const handleTableScroll = (event) => {
        const target = event?.currentTarget || event?.target;
        if (!target || typeof target.scrollTop !== 'number') return;
        const distanceToBottom = target.scrollHeight - target.scrollTop - target.clientHeight;
        if (distanceToBottom < 100) {
            loadMoreInstances();
        }
    };

    // 查看详情（直接使用列表数据，无需调用API）
    const handleViewDetail = (record) => {
        setDetailDrawerVisible(true);
        setSelectedInstance(record);
    };

    // 筛选条件变化（列表由 filters 的 useEffect 自动重置拉取）
    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
    };

    // 搜索功能
    const handleSearch = () => {
        fetchInstances({ page: 1, reset: true });
    };

    // 重置功能
    const handleReset = () => {
        setFilters({
            sales_route_id: null,
            complete_only: true,
            aircraft_reg: [],
            date_range: null,
        });
        setDateRangeKey(prev => prev + 1); // 强制重置日期范围组件
    };

    // 联动过滤后的下拉选项
    const routeOptions = useMemo(() => {
        const activeRoutes = routes.filter(r => r.status === 'ACTIVE');
        if (!linkedRouteIds) return activeRoutes;
        return activeRoutes.filter(r => linkedRouteIds.has(r.id));
    }, [routes, linkedRouteIds]);

    const aircraftOptions = useMemo(() => {
        if (!linkedAircraftRegs) return allAircrafts;
        return allAircrafts.filter(a => linkedAircraftRegs.has(a.registration));
    }, [allAircrafts, linkedAircraftRegs]);

    // 时区感知的时间格式化函数
    const formatTimeWithTimezone = (utcTime, departureAirport = null) => {
        if (!utcTime) return '-';
        
        try {
            // 获取目标时区
            const targetTimezone = getTimezoneByMode(timeZoneMode, departureAirport, airportsMap);
            
            // 使用TimezoneConverter进行转换
            const formattedTime = TimezoneConverter.toTargetTimezone(
                utcTime,
                targetTimezone,
                'YYYY-MM-DD HH:mm'
            );
            
            return formattedTime || '-';
        } catch (error) {
            console.error('时间格式化错误:', error);
            // 降级处理：使用本地时间
            return dayjs.utc(utcTime).local().format('YYYY-MM-DD HH:mm');
        }
    };
    
    // 格式化金额（带样式）
    const formatCost = (cost) => {
        if (!cost || cost === 0) {
            return null;
        }
        return `$${cost.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };

    // 格式化费用单元格（带底色样式）
    const renderCostCell = (cost) => {
        const hasValue = cost && cost > 0;
        return (
            <div style={{
                padding: '4px 8px',
                borderRadius: '4px',
                backgroundColor: hasValue ? '#e6f7ff' : '#fafafa',
                color: hasValue ? '#1890ff' : '#bfbfbf',
                fontWeight: hasValue ? '500' : 'normal',
                textAlign: 'center'
            }}>
                {hasValue ? formatCost(cost) : '-'}
            </div>
        );
    };

    // 格式化BH/FH单元格（时间单位）
    const renderBHCell = (value) => {
        const hasValue = value && value > 0;
        return (
            <div style={{
                padding: '4px 8px',
                borderRadius: '4px',
                backgroundColor: hasValue ? '#f0f5ff' : '#fafafa',
                color: hasValue ? '#597ef7' : '#bfbfbf',
                fontWeight: hasValue ? '500' : 'normal',
                textAlign: 'center'
            }}>
                {hasValue ? `${Number(value).toFixed(2)}H` : '-'}
            </div>
        );
    };

    // 航段明细列定义（展开行使用，与主表格对齐）
    const getLegDetailColumns = () => [
            {
                title: '航班号 - 航段',
                key: 'flight_leg',
                width: 180,  // 对齐主行的"航线代码"列
                align: 'center',
                render: (_, record) => (
                    <span style={{ fontWeight: 500 }}>
                        {record.flight_number} {record.leg}
                    </span>
                )
            },
            {
                title: () => `起飞时间 (${getTimeZoneDisplayName()})`,
                dataIndex: 'atd',
                key: 'atd',
                width: 150,  // 对齐主行的"执行时间"列
                align: 'center',
                render: (text, record) => (
                    <span style={{ whiteSpace: 'nowrap' }}>
                        {formatTimeWithTimezone(text, record.departure_airport)}
                    </span>
                )
            },
            {
                title: '',  // 空列，对齐主行的"飞机"列
                key: 'placeholder',
                width: 100,
                align: 'center',
                render: () => null
            },
            {
                title: '段次',
                dataIndex: 'leg_sequence',
                key: 'leg_sequence',
                width: 80,  // 对齐主行的"航段数"列
                align: 'center',
                render: (seq) => <Tag color="blue">第 {seq} 段</Tag>
            },
            {
                title: 'BH (小时)',
                dataIndex: 'bh',
                key: 'bh',
                width: 110,
                align: 'center',
                render: (value) => {
                    if (!value || value === 0) return null;
                    return (
                        <Text style={{ color: '#1890ff' }}>
                            {Number(value).toFixed(2)}H
                        </Text>
                    );
                }
            },
            {
                title: 'FH (飞行时间)',
                dataIndex: 'fh',
                key: 'fh',
                width: 120,
                align: 'center',
                render: (value) => {
                    if (!value || value === 0) return null;
                    return (
                        <Text style={{ color: '#1890ff' }}>
                            {Number(value).toFixed(2)}H
                        </Text>
                    );
                }
            },
            {
                title: 'ACMI',
                dataIndex: 'acmi',
                key: 'acmi',
                width: 110,
                align: 'center',
                render: formatCost
            },
            {
                title: 'Fuel',
                dataIndex: 'fuel',
                key: 'fuel',
                width: 110,
                align: 'center',
                render: formatCost
            },
            {
                title: 'Navigation',
                dataIndex: 'navigation',
                key: 'navigation',
                width: 130,
                align: 'center',
                render: formatCost
            },
            {
                title: 'Ground Handling',
                dataIndex: 'ground_handling',
                key: 'ground_handling',
                width: 140,
                align: 'center',
                render: formatCost
            },
            {
                title: 'Landing/Parking',
                dataIndex: 'landing_parking',
                key: 'landing_parking',
                width: 150,
                align: 'center',
                render: formatCost
            },
            {
                title: 'Catering',
                dataIndex: 'catering',
                key: 'catering',
                width: 120,
                align: 'center',
                render: formatCost
            },
            {
                title: 'Transportation',
                dataIndex: 'transportation',
                key: 'transportation',
                width: 140,
                align: 'center',
                render: formatCost
            },
            {
                title: 'Trucking Fee',
                dataIndex: 'trucking_fee',
                key: 'trucking_fee',
                width: 140,
                align: 'center',
                render: formatCost
            },
            {
                title: 'Cargo Handling',
                dataIndex: 'cargo_handling',
                key: 'cargo_handling',
                width: 140,
                align: 'center',
                render: formatCost
            },
            {
                title: 'Traffic Rights Permit',
                dataIndex: 'traffic_rights_permit',
                key: 'traffic_rights_permit',
                width: 180,
                align: 'center',
                render: formatCost
            },
            {
                title: 'Traffic Rights',
                dataIndex: 'traffic_rights',
                key: 'traffic_rights',
                width: 140,
                align: 'center',
                render: formatCost
            },
            {
                title: 'Positioning',
                dataIndex: 'positioning',
                key: 'positioning',
                width: 130,
                align: 'center',
                render: formatCost
            },
            {
                title: 'ULD',
                dataIndex: 'uld',
                key: 'uld',
                width: 100,
                align: 'center',
                render: formatCost
            },
            {
                title: 'SITA',
                dataIndex: 'sita',
                key: 'sita',
                width: 100,
                align: 'center',
                render: formatCost
            },
            {
                title: 'Deicing',
                dataIndex: 'deicing',
                key: 'deicing',
                width: 120,
                align: 'center',
                render: formatCost
            },
            {
                title: 'Accommodation',
                dataIndex: 'accommodation',
                key: 'accommodation',
                width: 150,
                align: 'center',
                render: formatCost
            },
            {
                title: 'Laundry',
                dataIndex: 'laundry',
                key: 'laundry',
                width: 120,
                align: 'center',
                render: formatCost
            },
            {
                title: 'Disinfection',
                dataIndex: 'disinfection',
                key: 'disinfection',
                width: 140,
                align: 'center',
                render: formatCost
            },
            {
                title: 'Flight Service Charge',
                dataIndex: 'flight_service_charge',
                key: 'flight_service_charge',
                width: 180,
                align: 'center',
                render: formatCost
            },
            {
                title: 'Others',
                dataIndex: 'others_fee',
                key: 'others_fee',
                width: 120,
                align: 'center',
                render: formatCost
            },
            {
                title: 'Commission',
                dataIndex: 'commission',
                key: 'commission',
                width: 130,
                align: 'center',
                render: formatCost
            },
            {
                title: 'Total(USD)',
                dataIndex: 'total_cost',
                key: 'total_cost',
                width: 140,
                align: 'center',
                fixed: 'right',
                render: (cost) => (
                    <Text strong style={{ color: '#e6a23c' }}>
                        {formatCost(cost)}
                    </Text>
                )
            }
    ];

    // 航段明细列定义（抽屉使用，与展开行保持一致）
    const getDrawerLegDetailColumns = () => [
            {
                title: '航班号 - 航段',
                key: 'flight_leg',
                width: 180,
                align: 'center',
                render: (_, record) => (
                    <span style={{ fontWeight: 500 }}>
                        {record.flight_number} {record.leg}
                    </span>
                )
            },
            {
                title: () => `起飞时间 (${getTimeZoneDisplayName()})`,
                dataIndex: 'atd',
                key: 'atd',
                width: 150,
                align: 'center',
                render: (text, record) => (
                    <span style={{ whiteSpace: 'nowrap' }}>
                        {formatTimeWithTimezone(text, record.departure_airport)}
                    </span>
                )
            },
            {
                title: '',  // 空列，对齐主行的"飞机"列
                key: 'placeholder',
                width: 100,
                align: 'center',
                render: () => null
            },
            {
                title: '段次',
                dataIndex: 'leg_sequence',
                key: 'leg_sequence',
                width: 80,  // 对齐主行的"航段数"列
                align: 'center',
                render: (seq) => <Tag color="blue">第 {seq} 段</Tag>
            },
            {
                title: 'BH (小时)',
                dataIndex: 'bh',
                key: 'bh',
                width: 110,
                align: 'center',
                render: renderBHCell
            },
            {
                title: 'FH (飞行时间)',
                dataIndex: 'fh',
                key: 'fh',
                width: 120,
                align: 'center',
                render: renderBHCell
            },
            {
                title: 'ACMI',
                dataIndex: 'acmi',
                key: 'acmi',
                width: 110,
                align: 'center',
                render: renderCostCell
            },
            {
                title: 'Fuel',
                dataIndex: 'fuel',
                key: 'fuel',
                width: 110,
                align: 'center',
                render: renderCostCell
            },
            {
                title: 'Navigation',
                dataIndex: 'navigation',
                key: 'navigation',
                width: 130,
                align: 'center',
                render: renderCostCell
            },
            {
                title: 'Ground Handling',
                dataIndex: 'ground_handling',
                key: 'ground_handling',
                width: 140,
                align: 'center',
                render: renderCostCell
            },
            {
                title: 'Landing/Parking',
                dataIndex: 'landing_parking',
                key: 'landing_parking',
                width: 150,
                align: 'center',
                render: renderCostCell
            },
            {
                title: 'Catering',
                dataIndex: 'catering',
                key: 'catering',
                width: 120,
                align: 'center',
                render: renderCostCell
            },
            {
                title: 'Transportation',
                dataIndex: 'transportation',
                key: 'transportation',
                width: 140,
                align: 'center',
                render: renderCostCell
            },
            {
                title: 'Trucking Fee',
                dataIndex: 'trucking_fee',
                key: 'trucking_fee',
                width: 140,
                align: 'center',
                render: renderCostCell
            },
            {
                title: 'Cargo Handling',
                dataIndex: 'cargo_handling',
                key: 'cargo_handling',
                width: 140,
                align: 'center',
                render: renderCostCell
            },
            {
                title: 'Traffic Rights Permit',
                dataIndex: 'traffic_rights_permit',
                key: 'traffic_rights_permit',
                width: 180,
                align: 'center',
                render: renderCostCell
            },
            {
                title: 'Traffic Rights',
                dataIndex: 'traffic_rights',
                key: 'traffic_rights',
                width: 140,
                align: 'center',
                render: renderCostCell
            },
            {
                title: 'Positioning',
                dataIndex: 'positioning',
                key: 'positioning',
                width: 130,
                align: 'center',
                render: renderCostCell
            },
            {
                title: 'ULD',
                dataIndex: 'uld',
                key: 'uld',
                width: 100,
                align: 'center',
                render: renderCostCell
            },
            {
                title: 'SITA',
                dataIndex: 'sita',
                key: 'sita',
                width: 100,
                align: 'center',
                render: renderCostCell
            },
            {
                title: 'Deicing',
                dataIndex: 'deicing',
                key: 'deicing',
                width: 120,
                align: 'center',
                render: renderCostCell
            },
            {
                title: 'Accommodation',
                dataIndex: 'accommodation',
                key: 'accommodation',
                width: 150,
                align: 'center',
                render: renderCostCell
            },
            {
                title: 'Laundry',
                dataIndex: 'laundry',
                key: 'laundry',
                width: 120,
                align: 'center',
                render: renderCostCell
            },
            {
                title: 'Disinfection',
                dataIndex: 'disinfection',
                key: 'disinfection',
                width: 140,
                align: 'center',
                render: renderCostCell
            },
            {
                title: 'Flight Service Charge',
                dataIndex: 'flight_service_charge',
                key: 'flight_service_charge',
                width: 180,
                align: 'center',
                render: renderCostCell
            },
            {
                title: 'Others',
                dataIndex: 'others_fee',
                key: 'others_fee',
                width: 120,
                align: 'center',
                render: renderCostCell
            },
            {
                title: 'Commission',
                dataIndex: 'commission',
                key: 'commission',
                width: 130,
                align: 'center',
                render: renderCostCell
            },
            {
                title: 'Total(USD)',
                dataIndex: 'total_cost',
                key: 'total_cost',
                width: 140,
                align: 'center',
                fixed: 'right',
                render: (cost) => (
                    <div style={{
                        padding: '4px 8px',
                        borderRadius: '4px',
                        backgroundColor: '#fff7e6',
                        border: '1px solid #ffd666',
                        fontWeight: '600',
                        color: '#d46b08',
                        fontSize: '14px'
                    }}>
                        {formatCost(cost)}
                    </div>
                )
            }
    ];

    // 航段明细展开内容
    const expandedRowRender = (record) => {
        return (
            <div style={{ padding: '0 16px' }}>
                <Table
                    columns={getLegDetailColumns()}
                    dataSource={record.legs || []}
                    rowKey={(leg) => `${record.route_instance_id}-${leg.leg_sequence}`}
                    pagination={false}
                    size="small"
                    scroll={{ x: 2800 }}
                    className="expanded-table"
                />
            </div>
        );
    };

    // 主表格列定义（包含所有费用类型）
    const columns = [
        {
            title: '航线代码',
            dataIndex: 'route_code',
            key: 'route_code',
            width: 180,
            fixed: 'left',
            align: 'center',
            ellipsis: {
                showTitle: false,
            },
            render: (text, record) => (
                <Tooltip title={record.route_name}>
                    <span style={{ fontWeight: 'bold', cursor: 'pointer' }}>
                        {text}
                    </span>
                </Tooltip>
            )
        },
        {
            title: () => (
                <span>
                    执行时间
                    <br />
                    <Text type="secondary" style={{ fontSize: '11px', fontWeight: 'normal' }}>
                        ({getTimeZoneDisplayName()})
                    </Text>
                </span>
            ),
            dataIndex: 'first_flight_time',
            key: 'first_flight_time',
            width: 150,
            align: 'center',
            sorter: (a, b) => {
                // 处理空值情况
                if (!a.first_flight_time && !b.first_flight_time) return 0;
                if (!a.first_flight_time) return 1;
                if (!b.first_flight_time) return -1;
                
                // 正常时间比较
                return dayjs.utc(a.first_flight_time).valueOf() - dayjs.utc(b.first_flight_time).valueOf();
            },
            defaultSortOrder: 'descend',
            render: (text) => formatTimeWithTimezone(text)
        },
        {
            title: '飞机',
            key: 'aircraft_reg',
            width: 100,
            align: 'center',
            render: (_, record) => {
                // 从航段明细中获取飞机号
                const aircraftRegs = [...new Set((record.legs || []).map(leg => leg.aircraft_reg).filter(Boolean))];
                if (aircraftRegs.length === 0) return '-';
                if (aircraftRegs.length === 1) {
                    return <AircraftTag registration={aircraftRegs[0]} />;
                }
                // 多个飞机号时显示数量
                return (
                    <Tooltip title={aircraftRegs.join(', ')}>
                        <Tag color="orange">{aircraftRegs.length}架</Tag>
                    </Tooltip>
                );
            }
        },
        {
            title: '航段数',
            dataIndex: 'leg_count',
            key: 'leg_count',
            width: 80,
            align: 'center',
            render: (count) => <span className="leg-count">{count || 0}</span>
        },
        // 费用类型列
        {
            title: 'BH (小时)',
            dataIndex: ['cost_summary', 'bh'],
            key: 'bh',
            width: 110,
            align: 'center',
            render: (value) => {
                if (!value || value === 0) return null;
                return (
                    <Text style={{ color: '#1890ff' }}>
                        {Number(value).toFixed(2)}H
                    </Text>
                );
            }
        },
        {
            title: 'FH (飞行时间)',
            dataIndex: ['cost_summary', 'fh'],
            key: 'fh',
            width: 120,
            align: 'center',
            render: (value) => {
                if (!value || value === 0) return null;
                return (
                    <Text style={{ color: '#1890ff' }}>
                        {Number(value).toFixed(2)}H
                    </Text>
                );
            }
        },
        {
            title: 'ACMI',
            dataIndex: ['cost_summary', 'acmi'],
            key: 'acmi',
            width: 110,
            align: 'center',
            render: formatCost
        },
        {
            title: 'Fuel',
            dataIndex: ['cost_summary', 'fuel'],
            key: 'fuel',
            width: 110,
            align: 'center',
            render: formatCost
        },
        {
            title: 'Navigation',
            dataIndex: ['cost_summary', 'navigation'],
            key: 'navigation',
            width: 130,
            align: 'center',
            render: formatCost
        },
        {
            title: 'Ground Handling',
            dataIndex: ['cost_summary', 'ground_handling'],
            key: 'ground_handling',
            width: 140,
            align: 'center',
            render: formatCost
        },
        {
            title: 'Landing/Parking',
            dataIndex: ['cost_summary', 'landing_parking'],
            key: 'landing_parking',
            width: 150,
            align: 'center',
            render: formatCost
        },
        {
            title: 'Catering',
            dataIndex: ['cost_summary', 'catering'],
            key: 'catering',
            width: 120,
            align: 'center',
            render: formatCost
        },
        {
            title: 'Transportation',
            dataIndex: ['cost_summary', 'transportation'],
            key: 'transportation',
            width: 140,
            align: 'center',
            render: formatCost
        },
        {
            title: 'Trucking Fee',
            dataIndex: ['cost_summary', 'trucking_fee'],
            key: 'trucking_fee',
            width: 140,
            align: 'center',
            render: formatCost
        },
        {
            title: 'Cargo Handling',
            dataIndex: ['cost_summary', 'cargo_handling'],
            key: 'cargo_handling',
            width: 140,
            align: 'center',
            render: formatCost
        },
        {
            title: 'Traffic Rights Permit',
            dataIndex: ['cost_summary', 'traffic_rights_permit'],
            key: 'traffic_rights_permit',
            width: 180,
            align: 'center',
            render: formatCost
        },
        {
            title: 'Traffic Rights',
            dataIndex: ['cost_summary', 'traffic_rights'],
            key: 'traffic_rights',
            width: 140,
            align: 'center',
            render: formatCost
        },
        {
            title: 'Positioning',
            dataIndex: ['cost_summary', 'positioning'],
            key: 'positioning',
            width: 130,
            align: 'center',
            render: formatCost
        },
        {
            title: 'ULD',
            dataIndex: ['cost_summary', 'uld'],
            key: 'uld',
            width: 100,
            align: 'center',
            render: formatCost
        },
        {
            title: 'SITA',
            dataIndex: ['cost_summary', 'sita'],
            key: 'sita',
            width: 100,
            align: 'center',
            render: formatCost
        },
        {
            title: 'Deicing',
            dataIndex: ['cost_summary', 'deicing'],
            key: 'deicing',
            width: 120,
            align: 'center',
            render: formatCost
        },
        {
            title: 'Accommodation',
            dataIndex: ['cost_summary', 'accommodation'],
            key: 'accommodation',
            width: 150,
            align: 'center',
            render: formatCost
        },
        {
            title: 'Laundry',
            dataIndex: ['cost_summary', 'laundry'],
            key: 'laundry',
            width: 120,
            align: 'center',
            render: formatCost
        },
        {
            title: 'Disinfection',
            dataIndex: ['cost_summary', 'disinfection'],
            key: 'disinfection',
            width: 140,
            align: 'center',
            render: formatCost
        },
        {
            title: 'Flight Service Charge',
            dataIndex: ['cost_summary', 'flight_service_charge'],
            key: 'flight_service_charge',
            width: 180,
            align: 'center',
            render: formatCost
        },
        {
            title: 'Others',
            dataIndex: ['cost_summary', 'others_fee'],
            key: 'others_fee',
            width: 120,
            align: 'center',
            render: formatCost
        },
        {
            title: 'Commission',
            dataIndex: ['cost_summary', 'commission'],
            key: 'commission',
            width: 130,
            align: 'center',
            render: formatCost
        },
        {
            title: '总成本 (USD)',
            dataIndex: 'total_cost',
            key: 'total_cost',
            width: 150,
            align: 'center',
            fixed: 'right',
            render: (cost) => (
                <Text strong style={{ fontSize: '16px', color: '#e6a23c' }}>
                    {formatCost(cost)}
                </Text>
            )
        },
        {
            title: '操作',
            key: 'action',
            width: 100,
            fixed: 'right',
            align: 'center',
            render: (_, record) => (
                <Space size="small" className="table-actions">
                    <Button
                        type="link"
                        size="small"
                        icon={<EyeOutlined />}
                        onClick={() => handleViewDetail(record)}
                    >
                        详情
                    </Button>
                </Space>
            )
        }
    ];

    return (
        <div className="page-container">
            {/* 页面标题 */}
            <div className="page-header-compact">
                <div className="page-title-compact">成本报表管理</div>
                <div className="page-action-compact">
                    <Space size="middle">
                        <Button
                            icon={<ExportOutlined />}
                            loading={exporting}
                            disabled={!total || total === 0}
                            onClick={async () => {
                                const hasFilter = filters.sales_route_id
                                    || (filters.aircraft_reg?.length > 0)
                                    || (filters.date_range?.length === 2);
                                if (!hasFilter) {
                                    const confirmed = window.confirm(
                                        `当前未设置筛选条件，将导出全部 ${total} 条记录，数据量较大可能较慢。\n确定要继续吗？`
                                    );
                                    if (!confirmed) return;
                                }

                                setExporting(true);
                                try {
                                    const params = {
                                        complete_only: filters.complete_only,
                                        page: 1,
                                        page_size: 10000,
                                    };
                                    if (filters.sales_route_id) params.sales_route_id = filters.sales_route_id;
                                    if (filters.aircraft_reg?.length > 0) params.aircraft_reg = filters.aircraft_reg;
                                    if (filters.date_range?.length === 2) {
                                        params.start_date = filters.date_range[0];
                                        params.end_date = filters.date_range[1];
                                    }

                                    const response = await settlementApi.routeInstances.getRouteInstances(params);
                                    const allData = response.data.items || [];

                                    if (allData.length === 0) {
                                        message.warning('没有数据可导出');
                                        return;
                                    }

                                    const dateRange = filters.date_range || [];
                                    const dateSuffix = dateRange.length === 2 ? `_${dateRange[0]}_${dateRange[1]}` : '';
                                    const legCount = allData.reduce((sum, r) => sum + (r.legs?.length || 0), 0);
                                    exportProfitReportToExcel(allData, {
                                        filename: `成本报表${dateSuffix}.xlsx`,
                                        formatTime: (t) => t ? dayjs.utc(t).format('YYYY-MM-DD HH:mm') : '',
                                    });
                                    message.success(`导出成功，共 ${allData.length} 条航线、${legCount} 个航段`);
                                } catch (error) {
                                    console.error('导出失败:', error);
                                    message.error('导出失败，请重试');
                                } finally {
                                    setExporting(false);
                                }
                            }}
                        >
                            导出
                        </Button>
                        <Button
                            type="primary"
                            icon={<DashboardOutlined />}
                            onClick={() => navigate('/settlement/statistics-dashboard')}
                        >
                            统计看板
                        </Button>
                    </Space>
                </div>
            </div>

            {/* 跨批次航线说明 */}
            <Alert
                message={
                    <span>
                        显示<strong>全航线成本</strong>（含跨批次/跨月航段），系统自动汇总完整成本。
                    </span>
                }
                type="info"
                showIcon
                banner
                closable
                style={{ marginBottom: 12 }}
            />

            {/* 筛选条件 */}
            <Card className="filter-card" size="small">
                <Row gutter={12} align="bottom">
                    <Col flex="220px">
                        <div style={{ marginBottom: 4 }}>
                            <Text type="secondary">飞机号</Text>
                        </div>
                        <AircraftSelector
                            mode="multiple"
                            placeholder="选择飞机号"
                            style={{ width: '100%' }}
                            allowClear
                            value={filters.aircraft_reg}
                            onChange={(value) => handleFilterChange('aircraft_reg', value)}
                            maxTagCount="responsive"
                            displayMode="simple"
                            aircrafts={aircraftOptions}
                            loading={filterOptionsLoading}
                        />
                    </Col>
                    <Col flex="180px">
                        <div style={{ marginBottom: 4 }}>
                            <Text type="secondary">航线</Text>
                        </div>
                        <Select
                            placeholder={filterOptionsLoading ? '匹配中...' : '选择航线'}
                            style={{ width: '100%' }}
                            allowClear
                            loading={filterOptionsLoading}
                            value={filters.sales_route_id}
                            onChange={(value) => handleFilterChange('sales_route_id', value)}
                        >
                            {routeOptions.map(route => (
                                <Option key={route.id} value={route.id}>
                                    {route.route_code}
                                </Option>
                            ))}
                        </Select>
                    </Col>
                    <Col flex="280px">
                        <div style={{ marginBottom: 4 }}>
                            <Text type="secondary">日期范围</Text>
                        </div>
                        <DatePicker.RangePicker
                            key={dateRangeKey}
                            placeholder={['开始日期', '结束日期']}
                            style={{ width: '100%' }}
                            value={filters.date_range && filters.date_range.length === 2
                                ? [dayjs(filters.date_range[0]), dayjs(filters.date_range[1])]
                                : null}
                            onChange={(dates) => {
                                if (!dates) {
                                    handleFilterChange('date_range', null);
                                    return;
                                }
                                if (dates.length === 2 && dates[0] && dates[1]) {
                                    handleFilterChange('date_range', [
                                        dates[0].format('YYYY-MM-DD'),
                                        dates[1].format('YYYY-MM-DD'),
                                    ]);
                                }
                            }}
                            format="YYYY-MM-DD"
                        />
                    </Col>
                    <Col flex="none">
                        <div style={{ marginBottom: 4 }}>
                            <Text type="secondary">完整性</Text>
                        </div>
                        <Checkbox
                            checked={filters.complete_only}
                            onChange={(e) => handleFilterChange('complete_only', e.target.checked)}
                            style={{ lineHeight: '32px' }}
                        >
                            只显示完整航线
                        </Checkbox>
                    </Col>
                    <Col flex="auto">
                        <div style={{ marginBottom: 4 }}>
                            <Text type="secondary">操作</Text>
                        </div>
                        <Space>
                            <Button
                                type="primary"
                                icon={<ReloadOutlined />}
                                onClick={handleSearch}
                                loading={loading}
                            >
                                搜索
                            </Button>
                            <Button onClick={handleReset}>
                                重置
                            </Button>
                            <Button
                                icon={<ReloadOutlined />}
                                onClick={() => fetchInstances({ page: 1, reset: true })}
                                loading={loading}
                            >
                                刷新
                            </Button>
                        </Space>
                    </Col>
                </Row>
            </Card>

            {/* 航线实例列表（可展开，表头冻结 + 滚动加载） */}
            <Card
                className="data-card"
                title={
                    <div className="profit-report-list-meta">
                        <span>航线实例列表</span>
                        <Text type="secondary" style={{ fontWeight: 400, marginLeft: 12 }}>
                            {total > 0
                                ? `已加载 ${instances.length} / ${total} 条`
                                : '暂无数据'}
                        </Text>
                    </div>
                }
            >
                <Table
                    size="small"
                    columns={columns}
                    dataSource={instances}
                    rowKey="route_instance_id"
                    loading={loading}
                    expandable={{
                        expandedRowRender,
                        rowExpandable: (record) => record.legs && record.legs.length > 0,
                    }}
                    pagination={false}
                    sticky
                    onScroll={handleTableScroll}
                    scroll={{ x: 3500, y: 'calc(100vh - 360px)' }}
                    locale={{
                        emptyText: (
                            <Empty
                                description="暂无数据，请先创建批次核算"
                            />
                        )
                    }}
                    className="profit-report-table"
                    footer={
                        total > 0
                            ? () => (
                                <div className="profit-report-scroll-footer">
                                    {loadingMore ? (
                                        <Spin size="small" />
                                    ) : hasMore ? (
                                        <Button type="link" onClick={loadMoreInstances}>
                                            向下滚动或点击加载更多
                                        </Button>
                                    ) : (
                                        <Text type="secondary">已加载全部 {total} 条</Text>
                                    )}
                                </div>
                            )
                            : undefined
                    }
                />
            </Card>

            {/* 详情抽屉 */}
            <Drawer
                title="航线实例详情"
                placement="right"
                onClose={() => setDetailDrawerVisible(false)}
                open={detailDrawerVisible}
                width={900}
            >
                {selectedInstance ? (
                    <div>
                        {/* 基本信息 */}
                        <Descriptions title="基本信息" bordered column={3} size="small">
                            <Descriptions.Item label="航线代码" span={1}>
                                <Text strong>{selectedInstance.route_code}</Text>
                            </Descriptions.Item>
                            <Descriptions.Item label="航段数量" span={1}>
                                <Text strong>{selectedInstance.leg_count} 段</Text>
                            </Descriptions.Item>
                            <Descriptions.Item label="总成本" span={1}>
                                <Text strong style={{ fontSize: '16px', color: '#1890ff' }}>
                                    ${selectedInstance.total_cost.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                                </Text>
                            </Descriptions.Item>
                            <Descriptions.Item label="执行时间" span={3}>
                                <Text strong>
                                    {selectedInstance.first_flight_time ? formatTimeWithTimezone(selectedInstance.first_flight_time) : '-'}
                                </Text>
                            </Descriptions.Item>
                        </Descriptions>

                        {/* 核心指标卡片 */}
                        <div className="route-metrics" style={{ margin: '16px 0' }}>
                            <Row gutter={16}>
                                <Col span={8}>
                                    <Card size="small" style={{ textAlign: 'center', backgroundColor: '#f0f5ff', border: '1px solid #adc6ff' }}>
                                        <div className="metric-label" style={{ marginBottom: '8px', color: '#597ef7', fontSize: '13px', fontWeight: '500' }}>
                                            <span style={{ marginRight: '4px' }}>⏱️</span>
                                            整体航线BH
                                        </div>
                                        <div className="metric-value" style={{ fontSize: '24px', fontWeight: 'bold', color: '#597ef7' }}>
                                            {selectedInstance.legs?.reduce((sum, leg) => sum + (leg.bh || 0), 0).toFixed(2)} 小时
                                        </div>
                                    </Card>
                                </Col>
                                <Col span={8}>
                                    <Card size="small" style={{ textAlign: 'center', backgroundColor: '#f6ffed', border: '1px solid #b7eb8f' }}>
                                        <div className="metric-label" style={{ marginBottom: '8px', color: '#52c41a', fontSize: '13px', fontWeight: '500' }}>
                                            <span style={{ marginRight: '4px' }}>🧭</span>
                                            总ACMI费用（占比）
                                        </div>
                                        <div className="metric-value acmi-metric" style={{ fontSize: '20px', fontWeight: 'bold' }}>
                                            <span className="amount" style={{ color: '#52c41a' }}>
                                                ${selectedInstance.legs?.reduce((sum, leg) => sum + (leg.acmi || 0), 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                                            </span>
                                            <span className="percentage" style={{ marginLeft: '8px', color: '#8c8c8c', fontSize: '16px' }}>
                                                ({((selectedInstance.legs?.reduce((sum, leg) => sum + (leg.acmi || 0), 0) / selectedInstance.total_cost) * 100).toFixed(2)}%)
                                            </span>
                                        </div>
                                    </Card>
                                </Col>
                                <Col span={8}>
                                    <Card size="small" style={{ textAlign: 'center', backgroundColor: '#fffbe6', border: '1px solid #ffe58f' }}>
                                        <div className="metric-label" style={{ marginBottom: '8px', color: '#faad14', fontSize: '13px', fontWeight: '500' }}>
                                            <span style={{ marginRight: '4px' }}>⛽</span>
                                            总加油费用（占比）
                                        </div>
                                        <div className="metric-value fuel-metric" style={{ fontSize: '20px', fontWeight: 'bold' }}>
                                            <span className="amount" style={{ color: '#faad14' }}>
                                                ${selectedInstance.legs?.reduce((sum, leg) => sum + (leg.fuel || 0), 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                                            </span>
                                            <span className="percentage" style={{ marginLeft: '8px', color: '#8c8c8c', fontSize: '16px' }}>
                                                ({((selectedInstance.legs?.reduce((sum, leg) => sum + (leg.fuel || 0), 0) / selectedInstance.total_cost) * 100).toFixed(2)}%)
                                            </span>
                                        </div>
                                    </Card>
                                </Col>
                            </Row>
                        </div>

                        <Divider />

                        {/* 航段明细 */}
                        <Title level={5}>航段明细</Title>
                        <Table
                            columns={getDrawerLegDetailColumns()}
                            dataSource={selectedInstance.legs || []}
                            rowKey={(leg) => `${selectedInstance.route_instance_id}-${leg.leg_sequence}`}
                            pagination={false}
                            size="small"
                            scroll={{ x: 2800 }}
                        />
                    </div>
                ) : null}
            </Drawer>
        </div>
    );
};

export default ProfitReportManagementPage;
